import { useState, useEffect } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Home, Users, Wine, FileText, LogOut, Menu, X, 
  TrendingUp, UserPlus, Clock, CheckCircle, XCircle,
  Mail, Phone, MapPin, Trash2, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Dashboard Overview Component
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#722F37]"></div>
      </div>
    );
  }

  const statCards = [
    { icon: Users, label: "Total de Leads", value: stats?.total_leads || 0, color: "bg-blue-500" },
    { icon: Wine, label: "Total de Produtores", value: stats?.total_producers || 0, color: "bg-purple-500" },
    { icon: Clock, label: "Produtores Pendentes", value: stats?.pending_producers || 0, color: "bg-yellow-500" },
    { icon: TrendingUp, label: "Leads este mês", value: stats?.leads_this_month || 0, color: "bg-green-500" },
  ];

  return (
    <div data-testid="dashboard-overview">
      <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-gray-800 mb-8">
        Visão Geral
      </h1>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <div 
            key={index}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-200"
            data-testid={`stat-card-${index}`}
          >
            <div className="flex items-center gap-4">
              <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-12 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="font-['Playfair_Display'] text-xl font-semibold text-gray-800 mb-4">
          Bem-vindo ao Painel Administrativo
        </h2>
        <p className="text-gray-600">
          Use o menu lateral para gerenciar leads, produtores e o conteúdo do site.
          Aqui você pode aprovar novos produtores, acompanhar cadastros e atualizar as informações da landing page.
        </p>
      </div>
    </div>
  );
};

// Leads Management Component
const LeadsManagement = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads`, {
        headers: getAuthHeader()
      });
      setLeads(response.data);
    } catch (error) {
      toast.error("Erro ao carregar leads");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/leads/${id}/status`, { status }, {
        headers: getAuthHeader()
      });
      toast.success("Status atualizado com sucesso");
      fetchLeads();
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const deleteLead = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/leads/${deleteId}`, {
        headers: getAuthHeader()
      });
      toast.success("Lead removido com sucesso");
      setDeleteId(null);
      fetchLeads();
    } catch (error) {
      toast.error("Erro ao remover lead");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "status-badge status-pending",
      contacted: "status-badge status-contacted",
      converted: "status-badge status-converted"
    };
    const labels = {
      pending: "Pendente",
      contacted: "Contactado",
      converted: "Convertido"
    };
    return <span className={badges[status] || badges.pending}>{labels[status] || status}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#722F37]"></div>
      </div>
    );
  }

  return (
    <div data-testid="leads-management">
      <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-gray-800 mb-8">
        Gestão de Leads
      </h1>
      
      {leads.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Nenhum lead cadastrado ainda</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Interesse</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                    <td className="font-medium">{lead.name}</td>
                    <td>
                      <a href={`mailto:${lead.email}`} className="text-[#722F37] hover:underline">
                        {lead.email}
                      </a>
                    </td>
                    <td>
                      <span className={`px-2 py-1 rounded text-sm ${lead.interest_type === 'comprar' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                        {lead.interest_type === 'comprar' ? 'Comprar' : 'Produtor'}
                      </span>
                    </td>
                    <td className="text-gray-600 text-sm">
                      {new Date(lead.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td>{getStatusBadge(lead.status)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedLead(lead)}
                          data-testid={`view-lead-${lead.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteId(lead.id)}
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lead Details Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display']">Detalhes do Lead</DialogTitle>
            <DialogDescription>Informações completas do cadastro</DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-500 text-sm">Nome</Label>
                <p className="font-medium">{selectedLead.name}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-sm">Email</Label>
                <p>
                  <a href={`mailto:${selectedLead.email}`} className="text-[#722F37] hover:underline">
                    {selectedLead.email}
                  </a>
                </p>
              </div>
              <div>
                <Label className="text-gray-500 text-sm">Interesse</Label>
                <p>{selectedLead.interest_type === 'comprar' ? 'Comprar Vinhos' : 'Ser Produtor'}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-sm">Status</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    size="sm"
                    variant={selectedLead.status === 'pending' ? 'default' : 'outline'}
                    onClick={() => { updateStatus(selectedLead.id, 'pending'); setSelectedLead(null); }}
                    className={selectedLead.status === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
                  >
                    Pendente
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedLead.status === 'contacted' ? 'default' : 'outline'}
                    onClick={() => { updateStatus(selectedLead.id, 'contacted'); setSelectedLead(null); }}
                    className={selectedLead.status === 'contacted' ? 'bg-blue-500 hover:bg-blue-600' : ''}
                  >
                    Contactado
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedLead.status === 'converted' ? 'default' : 'outline'}
                    onClick={() => { updateStatus(selectedLead.id, 'converted'); setSelectedLead(null); }}
                    className={selectedLead.status === 'converted' ? 'bg-green-500 hover:bg-green-600' : ''}
                  >
                    Convertido
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este lead? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteLead}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-lead"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Producers Management Component
const ProducersManagement = () => {
  const [producers, setProducers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducer, setSelectedProducer] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchProducers();
  }, []);

  const fetchProducers = async () => {
    try {
      const response = await axios.get(`${API_URL}/producers`, {
        headers: getAuthHeader()
      });
      setProducers(response.data);
    } catch (error) {
      toast.error("Erro ao carregar produtores");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status, notes = null) => {
    try {
      await axios.patch(`${API_URL}/producers/${id}/status`, { status, notes }, {
        headers: getAuthHeader()
      });
      toast.success(`Produtor ${status === 'approved' ? 'aprovado' : status === 'rejected' ? 'rejeitado' : 'atualizado'} com sucesso`);
      fetchProducers();
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const deleteProducer = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/producers/${deleteId}`, {
        headers: getAuthHeader()
      });
      toast.success("Produtor removido com sucesso");
      setDeleteId(null);
      fetchProducers();
    } catch (error) {
      toast.error("Erro ao remover produtor");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "status-badge status-pending",
      approved: "status-badge status-approved",
      rejected: "status-badge status-rejected"
    };
    const labels = {
      pending: "Pendente",
      approved: "Aprovado",
      rejected: "Rejeitado"
    };
    return <span className={badges[status] || badges.pending}>{labels[status] || status}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#722F37]"></div>
      </div>
    );
  }

  return (
    <div data-testid="producers-management">
      <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-gray-800 mb-8">
        Gestão de Produtores
      </h1>
      
      {producers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
          <Wine className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Nenhum produtor cadastrado ainda</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Contato</th>
                  <th>Região</th>
                  <th>Tipos de Vinho</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {producers.map((producer) => (
                  <tr key={producer.id} data-testid={`producer-row-${producer.id}`}>
                    <td className="font-medium">{producer.name}</td>
                    <td>
                      <div className="text-sm">
                        <a href={`mailto:${producer.email}`} className="text-[#722F37] hover:underline block">
                          {producer.email}
                        </a>
                        <span className="text-gray-500">{producer.phone}</span>
                      </div>
                    </td>
                    <td>{producer.region}</td>
                    <td>{producer.wine_types}</td>
                    <td>{getStatusBadge(producer.status)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedProducer(producer)}
                          data-testid={`view-producer-${producer.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {producer.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => updateStatus(producer.id, 'approved')}
                              data-testid={`approve-producer-${producer.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => updateStatus(producer.id, 'rejected')}
                              data-testid={`reject-producer-${producer.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteId(producer.id)}
                          data-testid={`delete-producer-${producer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Producer Details Dialog */}
      <Dialog open={!!selectedProducer} onOpenChange={() => setSelectedProducer(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display']">Detalhes do Produtor</DialogTitle>
            <DialogDescription>Informações completas do cadastro</DialogDescription>
          </DialogHeader>
          {selectedProducer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-sm">Nome</Label>
                  <p className="font-medium">{selectedProducer.name}</p>
                </div>
                {selectedProducer.company_name && (
                  <div>
                    <Label className="text-gray-500 text-sm">Empresa</Label>
                    <p className="font-medium">{selectedProducer.company_name}</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-sm flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email
                  </Label>
                  <a href={`mailto:${selectedProducer.email}`} className="text-[#722F37] hover:underline">
                    {selectedProducer.email}
                  </a>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Telefone
                  </Label>
                  <p>{selectedProducer.phone}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-sm flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Região
                  </Label>
                  <p>{selectedProducer.region}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm flex items-center gap-1">
                    <Wine className="w-3 h-3" /> Tipos de Vinho
                  </Label>
                  <p>{selectedProducer.wine_types}</p>
                </div>
              </div>
              {selectedProducer.description && (
                <div>
                  <Label className="text-gray-500 text-sm">Descrição</Label>
                  <p className="text-gray-700">{selectedProducer.description}</p>
                </div>
              )}
              <div>
                <Label className="text-gray-500 text-sm">Data de Cadastro</Label>
                <p>{new Date(selectedProducer.created_at).toLocaleString('pt-BR')}</p>
              </div>
              <div>
                <Label className="text-gray-500 text-sm">Status</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    size="sm"
                    variant={selectedProducer.status === 'pending' ? 'default' : 'outline'}
                    onClick={() => { updateStatus(selectedProducer.id, 'pending'); setSelectedProducer(null); }}
                    className={selectedProducer.status === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
                  >
                    Pendente
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedProducer.status === 'approved' ? 'default' : 'outline'}
                    onClick={() => { updateStatus(selectedProducer.id, 'approved'); setSelectedProducer(null); }}
                    className={selectedProducer.status === 'approved' ? 'bg-green-500 hover:bg-green-600' : ''}
                  >
                    Aprovado
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedProducer.status === 'rejected' ? 'default' : 'outline'}
                    onClick={() => { updateStatus(selectedProducer.id, 'rejected'); setSelectedProducer(null); }}
                    className={selectedProducer.status === 'rejected' ? 'bg-red-500 hover:bg-red-600' : ''}
                  >
                    Rejeitado
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este produtor? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteProducer}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-producer"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Content Management Component
const ContentManagement = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/content`);
      setContent(response.data);
    } catch (error) {
      toast.error("Erro ao carregar conteúdo");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/content`, content, {
        headers: getAuthHeader()
      });
      toast.success("Conteúdo atualizado com sucesso");
    } catch (error) {
      toast.error("Erro ao salvar conteúdo");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#722F37]"></div>
      </div>
    );
  }

  return (
    <div data-testid="content-management">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-gray-800">
          Gestão de Conteúdo
        </h1>
        <Button 
          onClick={handleSave}
          disabled={saving}
          data-testid="save-content-btn"
          className="bg-[#722F37] hover:bg-[#5a232b] text-white"
        >
          {saving ? "Salvando..." : "Salvar Alterações"}
        </Button>
      </div>
      
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="font-['Playfair_Display'] text-xl font-semibold text-gray-800 mb-4">
            Seção Hero
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Título Principal</Label>
              <Input 
                value={content?.hero_title || ""}
                onChange={(e) => setContent(prev => ({ ...prev, hero_title: e.target.value }))}
                className="mt-1"
                data-testid="input-hero-title"
              />
            </div>
            <div>
              <Label>Subtítulo</Label>
              <Textarea 
                value={content?.hero_subtitle || ""}
                onChange={(e) => setContent(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                className="mt-1"
                rows={2}
                data-testid="input-hero-subtitle"
              />
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="font-['Playfair_Display'] text-xl font-semibold text-gray-800 mb-4">
            Seção Sobre
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input 
                value={content?.about_title || ""}
                onChange={(e) => setContent(prev => ({ ...prev, about_title: e.target.value }))}
                className="mt-1"
                data-testid="input-about-title"
              />
            </div>
            <div>
              <Label>Texto</Label>
              <Textarea 
                value={content?.about_text || ""}
                onChange={(e) => setContent(prev => ({ ...prev, about_text: e.target.value }))}
                className="mt-1"
                rows={4}
                data-testid="input-about-text"
              />
            </div>
          </div>
        </div>

        {/* Producer Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="font-['Playfair_Display'] text-xl font-semibold text-gray-800 mb-4">
            Seção Produtor
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input 
                value={content?.producer_title || ""}
                onChange={(e) => setContent(prev => ({ ...prev, producer_title: e.target.value }))}
                className="mt-1"
                data-testid="input-producer-title"
              />
            </div>
            <div>
              <Label>Texto</Label>
              <Textarea 
                value={content?.producer_text || ""}
                onChange={(e) => setContent(prev => ({ ...prev, producer_text: e.target.value }))}
                className="mt-1"
                rows={2}
                data-testid="input-producer-text"
              />
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="font-['Playfair_Display'] text-xl font-semibold text-gray-800 mb-4">
            Informações de Contato
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Email de Contato</Label>
              <Input 
                value={content?.contact_email || ""}
                onChange={(e) => setContent(prev => ({ ...prev, contact_email: e.target.value }))}
                className="mt-1"
                data-testid="input-contact-email"
              />
            </div>
            <div>
              <Label>URL do Facebook</Label>
              <Input 
                value={content?.facebook_url || ""}
                onChange={(e) => setContent(prev => ({ ...prev, facebook_url: e.target.value }))}
                className="mt-1"
                data-testid="input-facebook-url"
              />
            </div>
            <div>
              <Label>URL do Instagram</Label>
              <Input 
                value={content?.instagram_url || ""}
                onChange={(e) => setContent(prev => ({ ...prev, instagram_url: e.target.value }))}
                className="mt-1"
                data-testid="input-instagram-url"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const navItems = [
    { path: "/admin", icon: Home, label: "Visão Geral", end: true },
    { path: "/admin/leads", icon: Users, label: "Leads" },
    { path: "/admin/producers", icon: Wine, label: "Produtores" },
    { path: "/admin/content", icon: FileText, label: "Conteúdo" },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside 
        className={`admin-sidebar fixed lg:static inset-y-0 left-0 z-50 w-64 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200 ease-in-out flex flex-col`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-['Playfair_Display'] text-xl font-bold text-white">
              O Caseirinho
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) => `admin-sidebar-item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="text-white/80 text-sm mb-3 px-2">
            {admin?.email}
          </div>
          <button
            onClick={handleLogout}
            className="admin-sidebar-item w-full justify-start"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-white shadow-sm h-16 flex items-center px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900"
            data-testid="mobile-sidebar-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="ml-auto flex items-center gap-4">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#722F37] hover:underline text-sm"
              data-testid="view-site-link"
            >
              Ver Site →
            </a>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="leads" element={<LeadsManagement />} />
            <Route path="producers" element={<ProducersManagement />} />
            <Route path="content" element={<ContentManagement />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
